<template>
    <div class="dashboard-root">
      <el-container class="dashboard-container">
        <el-aside :width="isExpand ? '200px' : '80px'" class="dashboard-aside">
          <div class="aside-content">
            <div class="logo-container" :class="{ 'collapsed': !isExpand }">
              <img src="../assets/DiCloudAccess.svg" :class="['logo', { 'collapsed-logo': !isExpand }]">
            </div>
            <el-menu default-active="1" class="el-menu-vertical-demo" :router="true"
              :collapse="!isExpand" :collapse-transition="false">
              <!-- 新增意向用户管理菜单项 -->
              <el-menu-item index="/interested-users" @click="handleMenuClick('interested-users')">
                <el-icon><User /></el-icon>
                <template #title>意向用户</template>
              </el-menu-item>
              <el-menu-item index="/auth-management" @click="handleMenuClick('auth-management')">
                <el-icon><Document /></el-icon>
                <template #title>
                  授权管理
                  <el-badge v-if="pendingCount > 0" :value="pendingCount" class="menu-badge" />
                </template>
              </el-menu-item>
              <el-menu-item index="/user-login-info" @click="handleMenuClick('user-login-info')">
                <el-icon><Location /></el-icon>
                <template #title>登录日志</template>
              </el-menu-item>
              <el-menu-item index="/bug-management" @click="handleMenuClick('bug-management')">
                <el-icon><icon-menu /></el-icon>
                <template #title>Bug 管理</template>
              </el-menu-item>
            </el-menu>
          </div>
        </el-aside>
        <el-container class="main-container">
          <el-header class="dashboard-header">
            <el-button class="dashboard-header__toggle" :icon="isExpand ? Fold : Expand" @click="changeExpand"></el-button>
            <span class="dashboard-header__title">
              DiCloudAccess软件管理系统
            </span>
            <el-button class="dashboard-header__theme" @click="onToggleTheme">
              <el-icon>
                <Sunny v-if="adminTheme === 'dark'" />
                <Moon v-else />
              </el-icon>
              {{ adminTheme === 'dark' ? '浅色' : '深色' }}
            </el-button>
          </el-header>
          <router-view /> <!-- 用于显示路由组件 -->
        </el-container>
      </el-container>
    </div>
  </template>
  
  <script setup>
  import { Expand, Fold, Document, Menu as IconMenu, Location, User, Sunny, Moon } from '@element-plus/icons-vue'
  import { ref, onMounted, watch } from 'vue';
  import { getAdminTheme, toggleAdminTheme } from '../js/adminTheme';
  import { useRoute } from 'vue-router';
  import authApi from '../js/connect';
  import { useIsMobile } from '../composables/useIsMobile';
  
  const { isMobile } = useIsMobile();
  const route = useRoute();
  const isExpand = ref(!isMobile.value); // 默认根据设备类型设置展开状态
  const adminTheme = ref(getAdminTheme());
  const pendingCount = ref(0);

  const fetchPendingCount = async () => {
    try {
      const response = await authApi.getAllUsers({ page: 1, per_page: 1 });
      pendingCount.value = response.data.pending_count || 0;
    } catch (error) {
      console.error('获取待授权数量失败:', error);
    }
  };

  const onToggleTheme = () => {
    adminTheme.value = toggleAdminTheme();
  };
  
  // 处理菜单点击
  const handleMenuClick = (view) => {
    if (isMobile.value) {
      isExpand.value = false;
    }
  };
  
  // 切换菜单展开状态
  const changeExpand = () => {
    isExpand.value = !isExpand.value;
  };
  
  // 监听窗口大小变化
  watch(isMobile, (newIsMobile) => {
    if (newIsMobile) {
      isExpand.value = false; // 移动端自动收起
    }
  });
  
  // 组件挂载时初始化
  onMounted(() => {
    if (isMobile.value) {
      isExpand.value = false;
    }
    fetchPendingCount();
  });

  watch(() => route.path, () => {
    fetchPendingCount();
  });
  </script>
  
  <style>
  * {
    margin: 0;
    padding: 0;
  }
  
  .logo-container {
    padding: 10px;
    text-align: center;
    flex-shrink: 0;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .logo-container.collapsed {
    padding: 5px;
  }
  
  .logo {
    width: 80%;
    max-height: 100%;
    transition: all 0.3s;
  }
  
  .collapsed-logo {
    width: 90%;
    padding: 2px;
  }
  
  .el-menu-vertical-demo{
    border-right: 0 !important;
  }

  .menu-badge {
    margin-left: 8px;
  }
  
  /* 移动端样式适配 */
  @media screen and (max-width: 768px) {
    .el-aside {
      transition: width 0.3s;
    }
  
    .logo-container {
      height: 50px;
      padding: 5px;
    }
  
    .logo {
      width: 70%;
    }
  
    .dashboard-header {
      padding: 0 12px;
      height: 56px;
    }
  
    .dashboard-header span {
      font-size: 15px;
    }
  
    .el-button {
      padding: 8px !important;
    }
  }
  
  /* 添加新的布局样式 */
  .dashboard-container {
    height: 100vh;
    background-color: var(--dca-bg);
  }

  .dashboard-header {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 64px;
    padding: 0 20px;
    background-color: var(--dca-surface);
    border-bottom: 1px solid var(--dca-border);
    color: var(--dca-text);
    font-size: 17px;
    font-weight: var(--dca-fw-semibold);
  }

  .dashboard-header__title {
    flex: 1;
  }

  .dashboard-header__toggle.el-button,
  .dashboard-header__theme.el-button {
    background: transparent;
    border: 1px solid var(--dca-border);
    color: var(--dca-text-muted);
  }

  .dashboard-header__toggle.el-button:hover,
  .dashboard-header__theme.el-button:hover {
    border-color: var(--dca-accent);
    color: var(--dca-accent);
    background: var(--dca-accent-soft);
  }

  .dashboard-aside {
    background-color: var(--dca-surface);
    border-right: 1px solid var(--dca-border);
    padding: 10px;
    box-sizing: border-box;
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 1000;
    transition: width 0.3s;
    overflow: hidden;
  }
  
  .aside-content {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  .el-menu-vertical-demo {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    border-right: none;
  }
  
  /* 自定义滚动条样式 */
  .el-menu-vertical-demo::-webkit-scrollbar {
    width: 6px;
  }
  
  .el-menu-vertical-demo::-webkit-scrollbar-thumb {
    background: var(--dca-border-strong);
    border-radius: 3px;
  }
  
  .el-menu-vertical-demo::-webkit-scrollbar-track {
    background: var(--dca-surface);
  }
  
  .main-container {
    margin-left: v-bind("isExpand ? '200px' : '80px'");
    transition: margin-left 0.3s;
  }
  
  /* 移动端适配 */
  @media screen and (max-width: 768px) {
    .dashboard-aside {
      transform: translateX(v-bind("isExpand ? '0' : '-100%'"));
      width: 200px !important;
    }
  
    .main-container {
      margin-left: 0;
    }
  }
  </style>
